import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import CniVerificationCard from "@/components/CniVerificationCard";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Gift, Car, Tag, Calendar, Ticket, Copy, CheckCircle2,
  ChevronRight, Heart, Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const KIND_ICON = {
  carpool_free: Car,
  merchant_code: Tag,
  event_discount: Calendar,
  event_free: Ticket,
};
const KIND_COLOR = {
  carpool_free: { bg: "bg-mint-100", text: "text-mint-700", dot: "bg-mint-600" },
  merchant_code: { bg: "bg-sun-100", text: "text-sun-700", dot: "bg-sun-500" },
  event_discount: { bg: "bg-plum-100", text: "text-plum-700", dot: "bg-plum-600" },
  event_free: { bg: "bg-pink-100", text: "text-pink-600", dot: "bg-pink-500" },
};

export default function MyAdvantagesPage() {
  const { user } = useAuth();
  const [perks, setPerks] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [tab, setTab] = useState("available");
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, r, dr] = await Promise.all([
        api.get("/me/perks"),
        api.get("/me/redemptions"),
        api.get("/me/driver-rewards"),
      ]);
      setPerks(p.data);
      setRedemptions(r.data || []);
      setRewards(dr.data || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user) load(); }, [user]);

  const claim = async (perk) => {
    setBusy(perk.id);
    try {
      const r = await api.post(`/me/perks/${perk.id}/claim`);
      toast.success(`Code : ${r.data.code} — copié dans le presse-papier`);
      navigator.clipboard?.writeText(r.data.code).catch(() => {});
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  if (!user) {
    return (
      <PageShell>
        <div className="max-w-2xl mx-auto px-5 py-12 text-center">
          <h1 className="heading-display text-2xl text-plum-900 mb-2">Connectez-vous pour voir vos avantages</h1>
          <Link to="/login" className="rounded-full bg-plum-700 hover:bg-plum-800 text-white px-5 py-3 text-[13px] font-bold inline-flex items-center gap-2">
            Se connecter <ChevronRight size={14} />
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-6">
          <div className="phenix-eyebrow mb-2 inline-flex items-center gap-1.5">
            <Sparkles size={11} /> Mes avantages citoyens
          </div>
          <h1 className="heading-display text-[1.9rem] sm:text-[2.4rem] text-plum-900 leading-tight">
            Vos avantages <span className="text-mint-600">Istréens</span>
          </h1>
          <p className="text-[14px] text-plum-700/75 mt-2 max-w-2xl">
            La mairie attribue des avantages selon votre profil (sénior, jeune, étudiant, famille…). Trajets gratuits, codes remise chez nos commerçants, places offertes sur les événements.
          </p>
        </div>

        {/* Vérif CNI — toujours visible en haut */}
        {(!perks || perks.needs_cni) && (
          <div className="mb-6">
            <CniVerificationCard />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto mb-5">
          <TabBtn active={tab === "available"} onClick={() => setTab("available")} icon={Gift}>Disponibles</TabBtn>
          <TabBtn active={tab === "mine"} onClick={() => setTab("mine")} icon={Ticket} count={redemptions.length}>Mes codes</TabBtn>
          <TabBtn active={tab === "rewards"} onClick={() => setTab("rewards")} icon={Trophy} count={rewards.length}>Récompenses chauffeur</TabBtn>
        </div>

        {loading ? (
          <div className="phenix-card p-10 animate-pulse h-40" />
        ) : tab === "available" ? (
          <AvailableTab perks={perks?.perks || []} groups={perks?.groups || []} needsCni={perks?.needs_cni} onClaim={claim} busy={busy} />
        ) : tab === "mine" ? (
          <RedemptionsTab items={redemptions} />
        ) : (
          <RewardsTab items={rewards} />
        )}
      </div>
    </PageShell>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, count }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-[13px] font-bold transition-all ${
        active ? "bg-plum-700 text-white shadow-md" : "bg-white text-plum-700 border border-cream-200 hover:bg-cream-50"
      }`}
    >
      <Icon size={13} /> {children}
      {typeof count === "number" && count > 0 && (
        <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${active ? "bg-white/25" : "bg-cream-100 text-plum-700"}`}>
          {count}
        </span>
      )}
    </button>
  );
}

function AvailableTab({ perks, groups, needsCni, onClaim, busy }) {
  if (needsCni) {
    return (
      <div className="phenix-card bg-cream-50/60 p-8 text-center" data-testid="needs-cni-state">
        <div className="w-16 h-16 rounded-2xl bg-plum-100 text-plum-700 grid place-items-center mx-auto mb-3">
          <Sparkles size={28} />
        </div>
        <h3 className="font-display font-extrabold text-plum-900 text-lg mb-1">Validez votre CNI pour débloquer ces avantages</h3>
        <p className="text-[13px] text-plum-700/65 max-w-md mx-auto">
          Pour bénéficier des avantages, la mairie doit valider votre CNI et vous classer dans vos groupes citoyens. Utilisez la carte ci-dessus pour la déposer — vous serez notifié sous 48 h.
        </p>
      </div>
    );
  }
  if (!perks.length) {
    return (
      <div className="phenix-card bg-white p-8 text-center">
        <Gift size={26} className="mx-auto text-plum-700/30 mb-2" />
        <h3 className="font-display font-bold text-plum-900 mb-1">Aucun avantage actif pour vos groupes</h3>
        <p className="text-[13px] text-plum-700/65">
          Vos groupes actuels : {groups.length > 0 ? groups.map((g) => CniVerificationCard.GROUP_LABELS[g] || g).join(", ") : "—"}.
          <br />La mairie ajoutera bientôt de nouveaux avantages.
        </p>
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="available-perks">
      {perks.map((p) => {
        const Icon = KIND_ICON[p.kind] || Gift;
        const c = KIND_COLOR[p.kind] || KIND_COLOR.merchant_code;
        const exhausted = p.remaining_this_month <= 0;
        return (
          <article key={p.id} className="phenix-card phenix-card-hover bg-white p-4 sm:p-5 flex flex-col" data-testid={`perk-${p.id}`}>
            <div className="flex items-start gap-3 mb-3">
              <div className={`shrink-0 w-12 h-12 rounded-xl ${c.dot} text-white grid place-items-center text-2xl`}>
                {p.icon || <Icon size={22} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2 mb-0.5">
                  <h3 className="font-display font-extrabold text-plum-900 text-[15px] leading-tight">{p.title}</h3>
                </div>
                <span className={`phenix-pill ${c.bg} ${c.text} text-[10px] inline-flex items-center gap-1`}>
                  <Icon size={10} /> {p.kind_label}
                </span>
              </div>
            </div>
            <p className="text-[13px] text-plum-700/75 leading-snug mb-3 flex-1">{p.description}</p>
            <div className="flex items-center justify-between border-t border-cream-100 pt-3 mt-auto">
              <div className="text-[11px] text-plum-700/60">
                <strong className={exhausted ? "text-pink-500" : "text-plum-900"}>
                  {p.remaining_this_month}/{p.monthly_quota}
                </strong>{" "}restant ce mois
              </div>
              {p.kind === "carpool_free" ? (
                <Link
                  to="/covoiturage"
                  className="rounded-full bg-mint-600 hover:bg-mint-700 text-white px-4 py-2 text-[12px] font-bold inline-flex items-center gap-1.5"
                  data-testid={`use-on-trip-${p.id}`}
                >
                  Utiliser sur un trajet <ChevronRight size={12} />
                </Link>
              ) : (
                <Button
                  onClick={() => onClaim(p)}
                  disabled={busy === p.id || exhausted}
                  className="rounded-full bg-plum-700 hover:bg-plum-800 text-white h-9 px-4 text-[12px] font-bold disabled:opacity-40"
                  data-testid={`claim-${p.id}`}
                >
                  {busy === p.id ? "…" : exhausted ? "Épuisé" : "Récupérer le code"}
                </Button>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function RedemptionsTab({ items }) {
  if (!items.length) {
    return (
      <div className="phenix-card bg-white p-8 text-center">
        <Ticket size={26} className="mx-auto text-plum-700/30 mb-2" />
        <h3 className="font-display font-bold text-plum-900 mb-1">Aucun code récupéré pour le moment</h3>
        <p className="text-[13px] text-plum-700/65">Récupérez un avantage dans l&apos;onglet « Disponibles » pour obtenir un code.</p>
      </div>
    );
  }
  return (
    <ul className="space-y-2.5" data-testid="redemptions-list">
      {items.map((r) => (
        <li key={r.id} className="phenix-card bg-white p-4 flex items-center gap-3" data-testid={`redemption-${r.id}`}>
          <div className={`w-10 h-10 rounded-xl ${KIND_COLOR[r.kind]?.dot || "bg-plum-700"} text-white grid place-items-center`}>
            {(() => { const I = KIND_ICON[r.kind] || Gift; return <I size={18} />; })()}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-display font-bold text-plum-900 text-[14px] line-clamp-1">{r.perk_title}</h4>
            <p className="text-[11px] text-plum-700/55">{new Date(r.created_at).toLocaleString("fr-FR")}</p>
          </div>
          <CodeCopy code={r.code} status={r.status} />
        </li>
      ))}
    </ul>
  );
}

function RewardsTab({ items }) {
  if (!items.length) {
    return (
      <div className="phenix-card bg-white p-8 text-center">
        <Trophy size={26} className="mx-auto text-plum-700/30 mb-2" />
        <h3 className="font-display font-bold text-plum-900 mb-1">Pas encore de récompense</h3>
        <p className="text-[13px] text-plum-700/65 max-w-md mx-auto">
          Devenez Conducteur Solidaire : acceptez des candidats avec un avantage solidaire dans /covoiturage et la mairie vous récompensera (code remise commerçant ou place événement) !
        </p>
      </div>
    );
  }
  return (
    <ul className="space-y-2.5" data-testid="rewards-list">
      {items.map((r) => (
        <li key={r.id} className="phenix-card bg-gradient-to-r from-sun-50 to-cream-50 p-4 flex items-center gap-3" data-testid={`reward-${r.id}`}>
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sun-400 to-sun-500 text-white grid place-items-center">
            <Heart size={20} strokeWidth={2.4} fill="currentColor" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-display font-bold text-plum-900 text-[14px] leading-tight">{r.title}</h4>
            <p className="text-[11px] text-plum-700/65 mt-0.5">
              Gagnée en transportant <strong>{r.passenger_pseudo}</strong> · {new Date(r.created_at).toLocaleDateString("fr-FR")}
            </p>
          </div>
          <CodeCopy code={r.code} status={r.status} />
        </li>
      ))}
    </ul>
  );
}

function CodeCopy({ code, status }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      toast.success("Code copié");
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={onCopy}
      className="shrink-0 rounded-lg bg-white border border-cream-200 hover:bg-plum-50 px-3 py-2 inline-flex items-center gap-2 text-[12px] font-mono font-bold text-plum-900"
      data-testid={`copy-${code}`}
    >
      {copied ? <CheckCircle2 size={13} className="text-mint-600" /> : <Copy size={13} className="text-plum-700/55" />}
      <span>{code}</span>
      {status === "used" && <span className="text-[10px] text-plum-700/45 ml-1">utilisé</span>}
    </button>
  );
}
